import services from '../services';
import {apiActionImproved} from '../api';
import {DrawType, UserRole} from '../constants';
import {isPassGate, queuePostCreateOffer} from '../pricing/organizerPathRules.ts';

// Carries a tournament the user built on a free generator page (teams + format) through signup and
// into a real ScorePal tournament, so nothing is retyped. Stashed in localStorage (survives the
// signup redirect), then replayed by the /start/from-generator handler once the user is authenticated.

const KEY = 'scorepal_pending_generator';

/**
 * Back from "Continue with Google": does this account still need the organiser signup (role +
 * community) before the build? The bare account the socialite callback makes for a new mailbox,
 * and a PLAYER account with no club — an existing mailbox signing in to organise. Treating the
 * player as complete built the event as a club-less player, locked out of everything (Oct 2026).
 */
export function needsOrganizerCompletion(user: any): boolean {
    if (!user) return false;
    if (+user.role === UserRole.EmptyAfterSignup) return true;
    return +user.role === UserRole.Player && !user.club?.uid && !user.community?.uid;
}

export type GeneratorFormat = 'groups_knockout' | 'round_robin' | 'knockout' | 'double_elim' | 'swiss' | 'americano';

export interface GeneratorHandoff {
    source: string;               // which generator page it came from
    format: GeneratorFormat;
    sport: string;
    teams: string[];
    title?: string;
    groupCount?: number;          // groups_knockout only
    qualifiers?: number;          // groups_knockout only
    rounds?: number;              // swiss only
    playType?: 'singles' | 'doubles' | 'teams'; // quiz builder — drives participants_type
    teamSport?: boolean;          // quiz builder — team-shaped event (listed team sport, or a typed sport declared as teams)
    mode?: 'basic' | 'advanced';  // quiz builder — straight-to-draw vs full setup page
    eventKind?: 'tournament' | 'league' | 'americano'; // quiz builder — 'americano' hands off to the Americano setup, no draw
    leagueFormat?: 'singles' | 'doubles_individual' | 'teams'; // quiz league — participants_type
    pointsPerWin?: number;        // quiz football league — points table allocation
    pointsPerDraw?: number;       // quiz football league — points table allocation
    rankBy?: 'win_percent' | 'points'; // quiz league — standings on a points table or win %
    fixtures?: 'auto' | 'manual';      // quiz league — build the round robin now, or add matches by hand
}

const nextPowerOfTwo = (n: number): number => { let p = 1; while (p < n) p *= 2; return Math.max(2, p); };
const suggestedSwissRounds = (teamCount: number): number => Math.max(1, Math.ceil(Math.log2(Math.max(2, teamCount))));

export function stashGeneratorTournament(payload: GeneratorHandoff): void {
    try { localStorage.setItem(KEY, JSON.stringify(payload)); } catch { /* private mode / disabled storage */ }
}

export function readGeneratorHandoff(): GeneratorHandoff | null {
    try { const raw = localStorage.getItem(KEY); return raw ? JSON.parse(raw) as GeneratorHandoff : null; } catch { return null; }
}

export function clearGeneratorHandoff(): void {
    try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

const descFor = (f: GeneratorFormat) =>
    f === 'groups_knockout' ? 'Round Robin + Elimination'
        : f === 'knockout' ? 'Elimination'
            : f === 'double_elim' ? 'Double Elimination'
                : f === 'swiss' ? 'Swiss'
                    : 'Round Robin';

/**
 * Replays a handoff into a real tournament: creates it, adds the teams by name, and generates the draw
 * matching the chosen format. Returns the new tournament uid. Mirrors the Quick wizard's create+draw.
 */
export async function createFromHandoff(userUid: string, data: GeneratorHandoff, onStatus?: (s: string) => void): Promise<string> {
    const elimType = data.format === 'knockout' || data.format === 'double_elim';

    onStatus?.('Creating your tournament…');
    const res: any = await services.tournaments.create({
        user_uid:          userUid,
        title:             data.title || 'My Tournament',
        sport_type:        data.sport || 'padel',
        tournament_type:   elimType ? 2 : 1,
        draws_description: descFor(data.format),
        participants_type: 2, // team-by-name entries
        sets_count:        1,
        visibility_type:   1,
    } as any);
    const uid = res?.uid as string | undefined;
    if (!uid) throw new Error(res?.error || res?.message || 'Could not create the tournament');

    onStatus?.('Adding your teams…');
    for (const raw of (data.teams || [])) {
        const name = (raw || '').trim();
        if (name) await apiActionImproved('teams-create', {tournament_uid: uid, name});
    }

    onStatus?.('Generating the draw…');
    const current: any = await services.tournaments.find(uid);
    await services.tournaments.update(uid, {
        tournament_type:   elimType ? 2 : 1,
        draws_description: descFor(data.format),
        version:           (current?.version || 1) + 1,
    } as any);

    const count = (data.teams || []).length;
    const draw = async (params: any) => {
        const r: any = await services.tournaments.createDraw({uid, draw: params} as any);
        if (r?.error) throw Object.assign(new Error(r.error), r); // keep the structured `code`
    };

    try {
        await generate();
    } catch (err) {
        // The one-off pass gate (an already-signed-in one-off organizer with a field over the
        // free cover) is never an error on this pre-app surface: the tournament and its teams
        // exist, so hand over to the event page, where the offer opens once.
        if (!isPassGate(err)) throw err;
        queuePostCreateOffer(uid, err);
        return uid;
    }

    queuePostCreateOffer(uid); // a no-op unless the server says this event needs a pass
    return uid;

    async function generate() {
        if (data.format === 'knockout') {
            await draw({draw_type: DrawType.Elimination, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        } else if (data.format === 'double_elim') {
            await draw({draw_type: DrawType.DoubleElimination, teams_count: nextPowerOfTwo(count), winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        } else if (data.format === 'round_robin') {
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
        } else if (data.format === 'swiss') {
            const rounds = Math.min(Math.max(1, data.rounds || suggestedSwissRounds(count)), Math.max(1, count - 1));
            await draw({draw_type: DrawType.Swiss, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false, rounds});
        } else {
            const pools = Math.max(1, Math.min(data.groupCount || 2, Math.floor(count / 2)));
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: pools, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
            const koTeams = Math.max(2, (data.groupCount || 2) * (data.qualifiers || 2));
            await draw({draw_type: DrawType.Elimination, teams_count: koTeams, winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        }
    }
}
